import Head from 'next/head'
import Script from 'next/script'
import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { getArticleDateTime } from 'utils/article-date-time'
import { getRequestJSON } from 'api/index/location-api'
import { getTestimonials } from 'api/user/profile-api'
import { getPtTestimonials, setPtTestimonials } from 'service/testimonial-store'
import { s3Static } from 'utils/constants'
import { ptSingaporeFaqs } from 'utils/faq'

import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerHead } from 'components/index/service-category/personal-trainer/PersonalTrainerHead'
import TrustedPartners from 'components/index/service-category/personal-trainer/TrustedPartners'
import TrainerValueProposition from 'components/index/service-category/personal-trainer/TrainerValueProposition'
import { PersonalTrainerBooking } from 'components/index/service-category/personal-trainer/PersonalTrainerBooking'
import { TrainerCustomerReviews } from 'components/index/service-category/personal-trainer/TrainerCustomerReviews'
import { DarkFaq } from 'components/index/service-category/personal-trainer/DarkFaq'

import Link from 'next/link'
import { openModal } from 'service/modal-service'
import { FaCheckCircle, FaShieldAlt, FaPercentage, FaRobot, FaUtensils, FaChartLine, FaStar, FaUsers, FaHandshake, FaHeart } from 'react-icons/fa'
import { PersonalTrainerWhyUs } from '@/index/service-category/personal-trainer/PersonalTrainerWhyUs'
import { localBusinessNode } from 'utils/local-business-schema'

// ─── Static Props ───
export async function getStaticProps() {
    const { modifiedDate, month, year } = getArticleDateTime()
    const countryName = "Singapore"
    const allRequestCategorySettings = await getRequestJSON(countryName)
    const testimonials = getPtTestimonials().length > 0 ? getPtTestimonials() : await getTestimonials("Fitness Trainer", 0, 6)
    setPtTestimonials(testimonials)
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

    return {
        props: {
            requestCategorySettings,
            countryName,
            modifiedDate,
            month,
            year,
            testimonials,
        },
        revalidate: 259200,
    }
}

// ─── Buddy Training Value Proposition Section ───
const BuddyValueProposition = () => {
    return (
        <section className="relative bg-slate-900 py-12 md:py-20 overflow-hidden">
            {/* Mesh gradient blobs */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-amber-400/8 rounded-full blur-3xl" />

            <div className="relative z-10 max-w-5xl mx-auto px-4 space-y-16">

                {/* ── 1. What is Buddy Training ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-orange-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 to-amber-400 flex items-center justify-center shadow-lg">
                                <FaUsers className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">What Is Buddy Training? — Train Together, Pay Less</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Buddy training is simple: <strong className="text-white/90">you and a friend share the same personal trainer during the same session</strong> — 
                                and each of you pays just <strong className="text-orange-400">70% of the solo personal training rate</strong>. Same certified trainer, same 
                                professional guidance, same quality workout — but at a significantly lower price per person.
                            </p>
                            <p>
                                Think of it like carpooling, but for fitness. The trainer&apos;s time is shared, so the cost is split. But unlike group classes 
                                where you&apos;re one of 15–20 people, buddy training keeps it intimate — just two people. Your trainer can still give 
                                personalised attention, correct your form, and adapt exercises to each person&apos;s ability.
                            </p>
                            <p>
                                It&apos;s the perfect option if you and your partner, spouse, friend, sibling, or colleague have been thinking about getting fit 
                                together but found personal training too expensive. With buddy training, that barrier is gone.
                            </p>
                            <p className="text-white/80 font-medium">
                                Two people. One trainer. 30% savings each. No compromise on quality.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── Image Break 1 ── */}
                <div className="rounded-3xl overflow-hidden border border-white/10">
                    <Image src={s3Static + "fitness-package6.jpg"} alt="Buddy personal training packages in Singapore" width={1200} height={600} className="w-full h-48 md:h-96 object-cover object-bottom" />
                </div>

                {/* ── 2. Why Train With a Buddy ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-green-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-green-500 to-emerald-400 flex items-center justify-center shadow-lg">
                                <FaHandshake className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Why Train With a Buddy? — Motivation That Actually Works</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Let&apos;s be real — <strong className="text-white/90">the hardest part of fitness isn&apos;t the workout. It&apos;s showing up consistently.</strong> 
                                How many times have you told yourself &quot;I&apos;ll start next Monday&quot; or skipped a session because no one was counting on you?
                            </p>
                            <p>
                                When you train with a buddy, that changes completely. You have someone who&apos;s expecting you to show up. Someone who&apos;ll 
                                text you the night before saying &quot;see you tomorrow at 7am.&quot; Someone who pushes you when you want to give up on the last set — 
                                and someone you push right back.
                            </p>
                            <p>
                                Research consistently shows that people who exercise with a partner are 
                                <strong className="text-orange-400"> significantly more likely to stick to their fitness routine</strong> compared to those who train alone. 
                                It&apos;s not just about accountability — it&apos;s about making the experience genuinely enjoyable.
                            </p>
                            <p>
                                There&apos;s also a healthy competitive element. When your buddy pushes out one more rep, you want to match it. When they hit a 
                                personal best, it fires you up to hit yours. This kind of friendly competition raises the intensity of every session 
                                without the trainer having to push as hard.
                            </p>
                            <p className="text-white/80 font-medium">
                                Working out alone takes discipline. Working out with a buddy takes a text message.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── 3. 70% Pricing — How It Works ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-violet-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-purple-500 to-violet-400 flex items-center justify-center shadow-lg">
                                <FaPercentage className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Save 30% Per Person — Transparent Buddy Pricing</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                With buddy training, each person pays <strong className="text-orange-400">just 70% of the solo personal training rate</strong>. 
                                That&apos;s a straight 30% discount per person — no catches, no minimum commitments beyond the standard packages.
                            </p>
                            <p>
                                Let&apos;s put that into real numbers. If a Signature solo session costs $96/session, your buddy training rate would be 
                                approximately <strong className="text-white/90">$67/session per person</strong>. For a 30-session package, that&apos;s a saving of 
                                nearly <strong className="text-white/90">$870 each</strong> compared to training solo.
                            </p>
                            <div className="flex items-start gap-3 bg-white/5 rounded-2xl p-4 border border-white/10">
                                <FaRobot className="text-orange-400 text-xl mt-0.5 shrink-0" />
                                <p className="text-white/70">
                                    <strong className="text-white/90">Same trainer. Same quality. Same everything.</strong> The only difference is you&apos;re sharing the session — 
                                    and the cost — with someone you actually want to train with.
                                </p>
                            </div>
                            <p>
                                And just like all our packages, buddy training prices are <strong className="text-white/90">fully transparent and listed on our website</strong>. 
                                No hidden GST. No surprise fees. No &quot;starts at&quot; bait. What you see is what each person pays.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── Image Break 2 ── */}
                <div className="rounded-3xl overflow-hidden border border-white/10">
                    <Image src={s3Static + "blog/health+and+fitness/functional-fitness-trainer.jpg"} alt="Functional fitness trainer working with clients in Singapore" width={1200} height={600} className="w-full h-48 md:h-72 object-cover" />
                </div>

                {/* ── 4. Free Consultation for Both ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-blue-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg">
                                <FaCheckCircle className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Free Consultation — For Both of You</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Just like our 1-on-1 training, buddy training starts with a <strong className="text-orange-400">completely free consultation</strong> for 
                                both participants. You and your buddy get a comprehensive assessment together — understanding your requirements, health history, 
                                fitness levels, diet habits, and individual goals — no payment, no credit card, no obligation whatsoever.
                            </p>
                            <p>
                                During the consultation, the trainer reviews both of your medical histories and physical limitations, assesses your current fitness levels, 
                                evaluates your lifestyle and diet habits, and designs a personalised training plan that works for both of you. It&apos;s also a chance 
                                for you both to see whether you enjoy training together and whether the trainer is the right fit.
                            </p>
                            <p className="text-white/80 font-medium">
                                Most companies don&apos;t even offer a free consultation for solo sessions — let alone for two people. We believe in earning your 
                                trust before asking for your money.
                            </p>
                            <div className="pt-2">
                                <Link 
                                    href="/sg/personal-trainer/what-to-expect-from-personal-training" 
                                    className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold text-sm transition-colors"
                                >
                                    See what&apos;s included in the free consultation →
                                </Link>
                            </div>
                        </div>
                    </div>
                </article>

                {/* ── 5. Who Is Buddy Training For ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-rose-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-700/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-rose-500 to-pink-400 flex items-center justify-center shadow-lg">
                                <FaHeart className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Who Is Buddy Training For?</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Buddy training is perfect for anyone who wants the benefits of personal training but prefers not to train alone — 
                                or simply wants to save money while getting expert guidance. Common buddy pairings include:
                            </p>
                            <ul className="space-y-3">
                                {[
                                    { pair: 'Couples', desc: 'Train together as part of your shared lifestyle — strengthen your relationship and your bodies at the same time.' },
                                    { pair: 'Friends', desc: 'Turn your catch-up sessions into fitness sessions. Same social time, but with visible results.' },
                                    { pair: 'Siblings', desc: 'Family accountability hits different. When your brother or sister is counting on you, cancelling isn\'t an option.' },
                                    { pair: 'Colleagues', desc: 'Before-work or lunch-hour sessions with a co-worker. Great for corporate wellness and team bonding.' },
                                    { pair: 'Parent & Child', desc: 'For parents who want to lead by example and teens who need guidance starting their fitness journey.' },
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3">
                                        <FaCheckCircle className="text-orange-400 mt-1 shrink-0" />
                                        <div>
                                            <strong className="text-white/90">{item.pair}</strong>
                                            <span className="text-white/50"> — {item.desc}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-white/80 font-medium">
                                If there are two of you who want to get fit — buddy training is the smartest way to do it.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── 6. Diet Plan & Progress Tracking ── */}
                <article className="relative bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 py-6 px-4 md:p-10 overflow-hidden">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-amber-700/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-800/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4" />
                    <div className="relative">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="min-w-10 h-10 rounded-xl bg-linear-to-br from-amber-500 to-yellow-400 flex items-center justify-center shadow-lg">
                                <FaUtensils className="text-white text-lg" />
                            </div>
                            <h3 className="text-xl md:text-2xl font-bold text-white">Individual Diet Plans &amp; Biweekly Progress Tracking — For Each Person</h3>
                        </div>
                        <div className="space-y-4 text-white/60 text-sm md:text-base leading-relaxed">
                            <p>
                                Even though you train together, your bodies and goals are different. That&apos;s why each buddy receives their own 
                                <strong className="text-orange-400"> personalised AI-generated diet plan</strong> tailored to their individual goals, dietary preferences, 
                                and body type. No shared meal plans — each person gets a plan that actually works for them.
                            </p>
                            <p>
                                The same goes for progress tracking. Every two weeks, your trainer records <strong className="text-white/90">individual body measurements, 
                                strength benchmarks, and workout adherence</strong> for each person separately. If one buddy is progressing faster, the trainer 
                                adjusts the intensity accordingly. If the other needs a different approach, they get it.
                            </p>
                            <p className="text-white/80 font-medium">
                                You share the session and the savings — but the personalisation stays individual.
                            </p>
                        </div>
                    </div>
                </article>

                {/* ── Quick Summary Cards ── */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    {[
                        { icon: '👥', label: '2-Pax Training', sub: 'Train with a friend' },
                        { icon: '💰', label: '30% Cheaper', sub: '70% of solo rate each' },
                        { icon: '🎁', label: 'Free Consultation', sub: 'For both participants' },
                        { icon: '🥗', label: 'Diet Plan', sub: 'Individual for each' },
                        { icon: '📊', label: 'Progress', sub: 'Tracked per person' },
                        { icon: '⭐', label: 'Same Trainer', sub: 'Certified & experienced' },
                    ].map((item, i) => (
                        <div key={i} className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-4 text-center hover:bg-white/10 hover:border-orange-400/30 transition-all duration-300">
                            <span className="text-2xl block mb-1">{item.icon}</span>
                            <p className="text-sm font-semibold text-white/90">{item.label}</p>
                            <p className="text-[10px] text-white/40">{item.sub}</p>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    )
}


// ─── Main Page Component ───
export default function BuddyTraining(props) {
    const { requestCategorySettings, modifiedDate, year, testimonials } = props
    const router = useRouter()

    const [requestForm, setRequestForm] = useState(false)
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const requestType = "Fitness Trainer"

    useEffect(() => {
        if (router.query.requestForm === "true") {
            setRequestForm(true)
            openModal(setRequestForm)
        }
    }, [router])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 })
        openModal(setRequestForm)
    }

    return (
        <>
            <Head>
                <title>Buddy Personal Trainer Singapore — Train Together &amp; Save 30% | StringsSG</title>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta httpEquiv="content-language" content="en-sg" />
                <meta name="description"
                    content="Buddy personal training in Singapore — train with a friend and each pay just 70% of the solo rate. Same certified personal trainer, free consultation for both, custom diet plans & biweekly progress tracking." />
                <meta name="keywords"
                    content="buddy training singapore, buddy personal trainer singapore, couple personal training, partner training singapore, affordable personal trainer singapore, 2 person personal training, group personal training singapore" />
                <meta name="author" content="StringsSG" />
                <meta name="geo.region" content="SG" />
                <meta name="geo.placename" content="Singapore" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:url" content="https://www.stringssg.com/sg/personal-trainer/buddy-training" />
                <meta property="og:type" content="article" />
                <meta property="og:title" content="Buddy Personal Trainer Singapore — Train Together & Save 30% | StringsSG" />
                <meta property="og:description" content="Buddy personal training in Singapore — train with a friend and each pay just 70% of the solo rate. Free consultation for both, custom diet plans & biweekly tracking." />
                <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/girls-personal-training.jpg" />
                <meta property="article:published_time" content="2026-03-20T07:00:00+00:00" />
                <meta property="article:modified_time" content={modifiedDate} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@stringssg" />
                <meta name="twitter:title" content="Buddy Personal Trainer Singapore — Train Together & Save 30%" />
                <meta name="twitter:description" content="Train with a friend, each pay 70% of solo rate. Free consultation for both, custom diet plans, biweekly tracking." />
                <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/health+and+fitness/girls-personal-training.jpg" />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/buddy-training" />
                <script type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html:
                        `{"@context":"https://schema.org","@graph":[{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/","name":"Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/","name":"Buddy Personal Training Singapore","url":"https://www.stringssg.com/sg/personal-trainer/buddy-training/"}}]},${JSON.stringify(localBusinessNode)},{"@type":"Service","@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/#service","name":"Buddy Personal Trainer Singapore","description":"2-pax buddy personal training sessions with certified trainers. Each person pays 70% of the solo rate. Includes free consultation for both, individual AI diet plans, and biweekly progress tracking per person.","provider":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"areaServed":"Singapore","serviceType":"Buddy Personal Training","offers":{"@type":"AggregateOffer","lowPrice":"59","highPrice":"74","priceCurrency":"SGD","offerCount":"18","availability":"https://schema.org/InStock"},"hasOfferCatalog":{"@type":"OfferCatalog","name":"Buddy Training Packages","itemListElement":[{"@type":"Offer","name":"Essential Buddy Package","description":"Certified trainers with less than 1 year experience. 70% of solo rate per person.","priceSpecification":{"@type":"PriceSpecification","price":"59-66","priceCurrency":"SGD","unitText":"per session per person"}},{"@type":"Offer","name":"Signature Buddy Package","description":"Experienced trainers with over 2 years of proven results. 70% of solo rate per person.","priceSpecification":{"@type":"PriceSpecification","price":"63-70","priceCurrency":"SGD","unitText":"per session per person"}},{"@type":"Offer","name":"Elite Buddy Package","description":"Top-tier trainers with 5+ years experience. 70% of solo rate per person.","priceSpecification":{"@type":"PriceSpecification","price":"67-74","priceCurrency":"SGD","unitText":"per session per person"}}]}},{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/#webpage","url":"https://www.stringssg.com/sg/personal-trainer/buddy-training/","name":"Buddy Personal Trainer Singapore — Train Together & Save 30% | StringsSG","description":"Buddy personal training in Singapore — train with a friend and each pay just 70% of the solo rate. Same certified personal trainer, free consultation for both, custom diet plans & biweekly progress tracking.","inLanguage":"en-SG","isPartOf":{"@id":"https://www.stringssg.com/#website"},"breadcrumb":{"@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/#breadcrumblist"},"datePublished":"2026-03-20T07:00:00+00:00","dateModified":"${modifiedDate}","mainEntity":{"@id":"https://www.stringssg.com/sg/personal-trainer/buddy-training/#service"}},{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","foundingDate":"2018","description":"Singapore's leading platform for personal training services since 2018.","sameAs":["https://www.facebook.com/StringsSG","https://www.linkedin.com/company/stringssg/","https://www.youtube.com/channel/UCOcyUdhVeOZf2kXTGtg9oHw/"]},{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG Singapore","inLanguage":"en-SG","publisher":{"@id":"https://www.stringssg.com/#organization"}}]}`
                    }}
                />
            </Head>

            <div>
                <RequestProvider>
                    {/* 1. Hero Section */}
                    <PersonalTrainerHead
                        heroImage={s3Static + "blog/health+and+fitness/girls-personal-training.jpg"}
                        title="Buddy Personal Trainer in Singapore"
                        heading="Buddy Personal Trainer in Singapore"
                        heroCopy="Train with a friend and each pay 70% of the solo rate — from $59/session per person, at your home, condo gym or a park."
                        setIsQuickBook={setIsQuickBook}
                        openRequestForm={openRequestForm}
                        requestForm={requestForm}
                        setRequestForm={setRequestForm}
                        isQuickBook={isQuickBook}
                        isCustomBook={isCustomBook}
                        requestType={requestType}
                        requestCategorySettings={requestCategorySettings}
                    />

                    {/* H1 Intro Section */}
                    <section className="relative bg-slate-900 py-10 md:py-16 overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-amber-400/10 rounded-full blur-3xl" />

                        <div className="relative z-10 max-w-5xl mx-auto px-2 text-center">
                            <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-6 tracking-tight leading-tight">
                                <strong>Train Together &amp; Save 30%</strong>
                            </h2>
                            <p className="text-white/50 text-base md:text-lg mx-auto leading-relaxed mb-8">
                                Bring a friend, spouse, or colleague — share a certified personal trainer and each pay just 70% of the solo rate.
                                Same quality training, same free consultation, same personalised diet plans. The only difference? You save more and have more fun.
                            </p>
                            <p className="text-white/50 text-base mx-auto leading-relaxed mb-8">
                                Want the underlying numbers? Our guide to{' '}
                                <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-400/90 hover:text-orange-400 underline">
                                    personal trainer rates in Singapore
                                </Link>{' '}
                                shows solo and buddy pricing side by side, so you can see exactly what sharing a trainer saves each person.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                {['30% Cheaper Per Person', 'Free Consultation for Both', 'Same Certified Trainer', 'Individual Diet Plans', 'Biweekly Tracking'].map((tag, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full text-xs font-medium text-white/70">
                                        <FaStar className="text-orange-400 text-[10px]" />
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ─── Buddy Value Propositions ─── */}
                    <BuddyValueProposition />

                    {/* ─── Comparison Table ─── */}
                    <section className="mx-auto">
                        <div className="relative bg-slate-900 py-10">
                            <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
                            <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-400/10 rounded-full blur-3xl" />
                            <div className="relative z-10 max-w-5xl mx-auto px-3">
                                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 mx-auto w-fit">
                                    <span className="text-sm">📊</span>
                                    <span className="text-xs font-medium text-white/70">Buddy vs Solo</span>
                                </div>
                                <h3 className="text-xl md:text-2xl font-extrabold text-white mb-6 text-center tracking-tight">
                                    Buddy Training vs Solo Training
                                </h3>
                                <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md">
                                    <table className="w-full text-xs md:text-sm border-collapse">
                                        <thead>
                                            <tr className="bg-linear-to-r from-orange-500 to-amber-500 text-white">
                                                <th className="py-2 px-2 md:py-3 md:px-4 text-left font-semibold">Feature</th>
                                                <th className="py-2 px-2 md:py-3 md:px-4 text-center font-semibold">Buddy Training</th>
                                                <th className="py-2 px-2 md:py-3 md:px-4 text-center font-semibold">Solo Training</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                { feature: 'Price Per Person', buddy: '✅ 70% of solo rate', solo: '💰 Full rate' },
                                                { feature: 'Free Consultation', buddy: '✅ For both', solo: '✅ Yes' },
                                                { feature: 'Trainer Attention', buddy: '✅ Shared (2 pax)', solo: '✅ 100% dedicated' },
                                                { feature: 'Custom Diet Plan', buddy: '✅ Individual each', solo: '✅ Individual' },
                                                { feature: 'Progress Tracking', buddy: '✅ Per person', solo: '✅ Per person' },
                                                { feature: 'Accountability', buddy: '🔥 Built-in buddy', solo: '⚠️ Self-driven' },
                                                { feature: 'Fun Factor', buddy: '🔥 Train together', solo: '😐 Train alone' },
                                                { feature: 'Motivation', buddy: '🔥 Friendly competition', solo: '⚠️ Trainer-only' },
                                            ].map((row, i) => (
                                                <tr key={i} className={`${i % 2 === 0 ? 'bg-white/5' : 'bg-white/2'} border-b border-white/5`}>
                                                    <td className="py-2 px-2 md:py-3 md:px-4 font-medium text-white/80">{row.feature}</td>
                                                    <td className="py-2 px-2 md:py-3 md:px-4 text-center text-green-400 font-medium">{row.buddy}</td>
                                                    <td className="py-2 px-2 md:py-3 md:px-4 text-center text-white/40">{row.solo}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ─── Trusted Partners ─── */}
                    {/* <TrustedPartners /> */}

                    {/* ─── Value Proposition (Locations + Goals) ─── */}
                    <TrainerValueProposition />

                    <PersonalTrainerWhyUs />

                    {/* ─── Packages (Buddy mode ON by default) ─── */}
                    <PersonalTrainerBooking setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} year={2026} isBuddyTraining={true} />

                    {/* ─── Customer Reviews ─── */}
                    <TrainerCustomerReviews testimonials={testimonials} />

                    {/* ─── FAQs ─── */}
                    <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                        <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/8 rounded-full blur-3xl" />
                        <div className="relative z-10 max-w-5xl mx-auto px-4">
                            <DarkFaq
                                title="Buddy Training FAQs"
                                questionsAnswers={ptSingaporeFaqs}
                            />
                        </div>
                    </section>

                </RequestProvider>
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
            <Script id="facebook-tracking" strategy="lazyOnload">
                {`!function(f,b,e,v,n,t,s)
                    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                    n.queue=[];t=b.createElement(e);t.async=!0;
                    t.src=v;s=b.getElementsByTagName(e)[0];
                    s.parentNode.insertBefore(t,s)}(window, document,'script',
                    'https://connect.facebook.net/en_US/fbevents.js');
                    fbq('init', '128634041116395');
                    fbq('track', 'PageView');`}
            </Script>
        </>
    )
}
